import { useState, useEffect, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (address: {
    street: string;
    postal_code: string;
    city: string;
    country: string;
    lat: number;
    lon: number;
  }) => void;
  placeholder?: string;
}

export function AddressAutocomplete({ value, onChange, onSelect, placeholder }: AddressAutocompleteProps) {
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [hasUserTyped, setHasUserTyped] = useState(false);
  const [lastSelectedValue, setLastSelectedValue] = useState('');

  // Fetch Mapbox token on mount
  useEffect(() => {
    const fetchToken = async () => {
      try {
        const { data } = await supabase.functions.invoke('get-mapbox-token');
        if (data?.token) {
          setMapboxToken(data.token);
        }
      } catch (error) {
        console.error('Failed to fetch Mapbox token:', error);
      }
    };
    fetchToken();
  }, []);

  const searchAddress = useCallback(async (query: string) => {
    if (query.length < 3 || !mapboxToken) {
      setSuggestions([]);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(query)}.json?access_token=${mapboxToken}&types=address&limit=5&country=SE,NO,DK,FI,DE,GB,FR,US,CA,AU,JP,CN,IN,BR,MX`
      );
      
      const data = await response.json();
      setSuggestions(data.features || []);
      setShowSuggestions(true);
    } catch (error) {
      console.error('Address search error:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  }, [mapboxToken]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (value && hasUserTyped && value !== lastSelectedValue) {
        searchAddress(value);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [value, searchAddress, hasUserTyped, lastSelectedValue]);

  const handleSelectSuggestion = (feature: any) => {
    const [lon, lat] = feature.center;
    const context = feature.context || [];
    
    const postalCode = context.find((c: any) => c.id.startsWith('postcode'))?.text || '';
    const city = context.find((c: any) => c.id.startsWith('place'))?.text || '';
    const countryCode = context.find((c: any) => c.id.startsWith('country'))?.short_code?.toUpperCase() || '';
    
    // Extract the street address (number + street name)
    const streetAddress = feature.place_name.split(',')[0] || feature.text;

    // Update the input field to show the selected address
    onChange(streetAddress);

    // Call onSelect to populate all form fields
    onSelect({
      street: streetAddress,
      postal_code: postalCode,
      city: city,
      country: countryCode,
      lat,
      lon,
    });

    setLastSelectedValue(streetAddress);
    setHasUserTyped(false);
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHasUserTyped(true);
    onChange(e.target.value);
  };

  return (
    <div className="relative">
      <Input
        value={value}
        onChange={handleInputChange}
        placeholder={placeholder || "Start typing address..."}
        onFocus={() => value && suggestions.length > 0 && setShowSuggestions(true)}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
      />
      
      {isLoading && (
        <div className="absolute right-3 top-3">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-auto">
          {suggestions.map((feature, index) => (
            <button
              key={index}
              type="button"
              className="w-full text-left px-4 py-2 text-foreground hover:bg-primary hover:text-primary-foreground text-sm transition-colors border-b border-border last:border-b-0"
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelectSuggestion(feature);
              }}
            >
              <div className="font-medium">{feature.text}</div>
              <div className="text-xs opacity-70">{feature.place_name}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
